import React, { useCallback, useContext, useEffect, useState } from 'react';

// Styles & Assets
import styles from './PollsPage.module.css';
import stylesCol from '../../styles/Columns.module.css';
import { CgPlayListAdd } from 'react-icons/cg';
import { RiFilterFill } from 'react-icons/ri';
import { MdEdit } from 'react-icons/md';
// Services
import { getFrequencyPoll } from '../../services/getPollAttribute';
import { getEnvironmentApiUrl } from '../../services/General';

// Context
import FiltersContext from '../../context/FiltersContext';
import ScoresContext from '../../context/ScoresContext';

// Hooks
import useSearchbar from '../../hooks/useSearchbar';
import useHttp from '../../hooks/useHttp';

// Components
import TablePollActions from '../../components/PollsPage/TablePollActions/TablePollActions';
import TablePollAssignButton from '../../components/PollsPage/TablePollAssignButton/TablePollAssignButton';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import Toolbar from '../../components/UI/Toolbar/Toolbar';
import Searchbar from '../../components/UI/Searchbar/Searchbar';
import Table from '../../components/UI/Table/Table';
import ButtonToolbar from '../../components/UI/ButtonToolbar/ButtonToolbar';
import PollsFilter from '../../components/PollsPage/PollsFilter/PollsFilter';
import AlertPoll from '../../components/PollsPage/AlertPoll/AlertPoll';
import EnvironmentsString from '../../components/Helpers/EnvironmentsString';
import EditColumnsPolls from '../../components/PollsPage/EditColumnsPolls/EditColumnsPolls';
import CheckboxGrid from '../../components/UI/CheckboxGrid/CheckboxGrid';

// Interfaces
import { Column } from '../../interfaces/Columns';
import { Poll } from '../../interfaces/Poll';
import { FilterGeneric } from '../../interfaces/Filters';

// Language
import { useTranslation } from 'react-i18next';

// Other
import { Link } from 'react-router-dom';
import { canUserUpdateInquiries, getInquiryTypes, getProfessionalInquiryType } from '../../services/Poll';

const PollsPage: React.FC = () => {
    const { t } = useTranslation();

    const { pollsFilters, dispatchPollsFilters } = useContext(FiltersContext);
    const { scores } = useContext(ScoresContext);

    const [selectedProfileIDs, setSelectedProfileIDs] = useState<number[]>(pollsFilters.profileIDs);
    const [selectedIDsFrequencies, setSelectedIDsFrequencies] = useState<number[]>(pollsFilters.frequencyIDs);
    const [selectedIDsViewTables, setSelectedIDsViewTables] = useState<number[]>(pollsFilters.showTablesIDs);

    const [polls, setPolls] = useState<Poll[]>([]);
    const { isLoading, error, sendRequest: getPolls } = useHttp<Poll[]>();

    const [hiddenColumnIDs, setHiddenColumnIDs] = useState<number[]>([]);

    const sortPollByScoresOrAlerts = useCallback((poll: Poll) => {
        if ((scores?.find(s => s.inquiry_id === poll.id)?.score_rules ?? []).length > 0) return '1';
        if ((poll.inquiry_alerts ?? []).length > 0) return '1';
        return '-1';
    }, [scores]);

    const baseColumns: Column<Poll>[] = [
        {
            id: 0,
            name: () => t('actions'),
            visible: !hiddenColumnIDs.includes(0),
            sortable: false,
            element: (poll) => (
                <TablePollActions
                    poll={poll}
                    setPolls={setPolls}
                />
            )
        },
        {
            id: 1,
            name: () => t('name'),
            visible: !hiddenColumnIDs.includes(1),
            sortable: true,
            cell: (poll) => `${poll?.name ?? ''}`
        },
        {
            id: 2,
            name: () => t('questions'),
            visible: !hiddenColumnIDs.includes(2),
            sortable: true,
            classBody: stylesCol.centerColBody,
            classHeader: stylesCol.centerColHeaderParagraph,
            cell: (poll) => `${poll?.questions?.length ?? 0}`
        },
        {
            id: 3,
            name: () => t('periodicity'),
            visible: !hiddenColumnIDs.includes(3),
            sortable: true,
            cell: (poll) => `${t(getFrequencyPoll(poll))}`
        },
        ...(window.config.DIFFERENCE_TESTPROM_POLLS
            ? [{
                id: 10,
                name: () => t('type'),
                visible: !hiddenColumnIDs.includes(10),
                sortable: true,
                classBody: stylesCol.centerColBody,
                classHeader: stylesCol.centerColHeaderParagraph,
                cell: (poll: Poll) => getProfessionalInquiryType(poll)
            } as Column<Poll>]
            : []),
        {
            id: 4,
            name: () => t('environment'),
            visible: !hiddenColumnIDs.includes(4),
            sortable: false,
            element: (poll) => poll.profile_ids
                ? <EnvironmentsString profileIDs={poll.profile_ids} />
                : <></>
        },
        {
            id: 9,
            name: () => t('response_interval_hours'),
            visible: !hiddenColumnIDs.includes(9),
            sortable: true,
            cell: (poll) => poll.validity_duration_hours?.toString() ?? '-'
        },
        {
            id: 5,
            name: () => t('show_in_tables'),
            visible: !hiddenColumnIDs.includes(5),
            sortable: true,
            classBody: stylesCol.centerColBody,
            classHeader: stylesCol.centerColHeaderParagraph,
            cell: (poll) => `${poll?.questions?.length ?? 0}`
        },
        ...(window.config.DIFFERENCE_TESTPROM_POLLS ?
            [{
                id: 6,
                name: () => t('type'),
                visible: true,
                sortable: false,
                classBody: stylesCol.centerColBody,
                classHeader: stylesCol.centerColHeaderParagraph,
                cell: (poll: Poll) => getProfessionalInquiryType(poll)
            }] : []),
        {
            id: 7,
            name: () => t('scores_and_alerts'),
            visible: !hiddenColumnIDs.includes(6),
            sortable: true,
            classBody: stylesCol.centerColBody,
            classHeader: stylesCol.centerColHeaderParagraph,
            sortElementFunc: sortPollByScoresOrAlerts,
            element: (poll) => poll.id !== undefined ? <AlertPoll poll={poll} /> : <></>
        },
        {
            id: 8,
            name: () => '',
            visible: !hiddenColumnIDs.includes(7),
            sortable: false,
            editVisiblilityDisabled: true,
            element: (poll) => (
                <TablePollAssignButton
                    poll={poll}
                    assign={true}
                    disabled={poll.is_profesional_inquiry === true}
                />
            )
        },
        {
            id: 8,
            name: () => '',
            visible: !hiddenColumnIDs.includes(8),
            sortable: false,
            editVisiblilityDisabled: true,
            element: (poll) => (
                <TablePollAssignButton
                    poll={poll}
                    assign={false}
                    disabled={getProfessionalInquiryType(poll) === 'TEST'}
                />
            )
        },
    ];

    const getFilter = useSearchbar(baseColumns);
    const [filter, setFilter] = useState<FilterGeneric<Poll>>(() => getFilter(''));

    useEffect(() => {
    getPolls({
        url: getEnvironmentApiUrl() + 'inquiry/all',
        method: 'POST',
        body: {
            params: {},
            filter: { enabled: true, modified: false, deleted: false }
        },
    }, (data) => {
        setPolls(data);
    });
}, [getPolls]);

    const handleChangeSearchbar = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(() => getFilter(evt.target.value));
    };

    const pollFilters = (poll: Poll) => {
        if (poll.frecuency === undefined) return false;
        /*return (pollsFilters.profileIDs.length === 0 || pollsFilters.profileIDs.some(id => poll.profile_ids?.includes(id)))
            && (pollsFilters.frequencyIDs.length === 0 || pollsFilters.frequencyIDs.some(freq => getFrequencyPoll(freq) === getFrequencyPoll(poll)))
            && (pollsFilters.typeIDs.length === 0 || pollsFilters.typeIDs.some(typeID => getFilterType(typeID) === getProfessionalInquiryType(poll)))
            && (pollsFilters.showTablesIDs.length === 0 || pollsFilters.showTablesIDs.some(id => (id === 0 && poll.has_table) || (id === 1 && !poll.has_table)));
*/
        return (
            (selectedProfileIDs.length === 0 || selectedProfileIDs.some(id => poll.profile_ids?.includes(id))) &&
            (selectedIDsFrequencies.length === 0 || selectedIDsFrequencies.some(freq => getFrequencyPoll(freq) === getFrequencyPoll(poll))) &&
            (selectedIDsViewTables.length === 0 || selectedIDsViewTables.some(id => (id === 0 && poll.has_table) || (id === 1 && !poll.has_table)))
        );
    };

    const filteredPolls = polls.filter(poll => filter()(poll) && pollFilters(poll));

    const handleConfirm = (profilesIDs: number[], frequenciesIDs: number[], showTablesIDs: number[]) => {
        setSelectedProfileIDs(profilesIDs);
        setSelectedIDsFrequencies(frequenciesIDs);
        setSelectedIDsViewTables(showTablesIDs);

        dispatchPollsFilters({ type: 'profileIDs', payload: profilesIDs });
        dispatchPollsFilters({ type: 'frequencyIDs', payload: frequenciesIDs });
        dispatchPollsFilters({ type: 'showTablesIDs', payload: showTablesIDs });
        //dispatchPollsFilters({ type: 'typeIDs', payload: typeIDs });
    };


    const changeVisibility = (id: number) => {
        setHiddenColumnIDs(oldIDs =>
            oldIDs.includes(id)
                ? oldIDs.filter(columnID => columnID !== id)
                : [...oldIDs, id]
        );
    };

    const disabledUpdateInquiries = !canUserUpdateInquiries();


    return (
        <div className='pageContainer'>
            <Toolbar>
                <div className={styles.buttonsRight}>
                    <Searchbar handleChange={handleChangeSearchbar} />

                    <ButtonToolbar
                        icon={<RiFilterFill />}
                        label={t('filter') + ` (${filteredPolls.length}/${polls.length})`}
                        defaultFilterActive={
                            selectedProfileIDs.length !== 0 ||
                            selectedIDsFrequencies.length !== 0 ||
                            selectedIDsViewTables.length !== 0
                        }
                    >
                        <PollsFilter
                            modalShow={() => undefined}
                            setFilterBallActive={() => undefined}
                            handleConfirm={handleConfirm}
                            defaultIDsEnvironment={pollsFilters.profileIDs}
                            defaultIDsFrequencies={pollsFilters.frequencyIDs}
                            defaultIDsViewTables={pollsFilters.showTablesIDs}
                            defaultIDsType={pollsFilters.typeIDs}
                        />
                    </ButtonToolbar>

                    <ButtonToolbar
                        icon={<MdEdit />}
                        label={t('custom_columns')}
                    >
                        <EditColumnsPolls
                            modalShow={() => undefined}
                        >
                            <CheckboxGrid
                                columns={baseColumns}
                                toggleVisibility={changeVisibility}
                            />
                        </EditColumnsPolls>
                    </ButtonToolbar>
                    <Link
                        to='create_poll'
                        onClick={(e) => disabledUpdateInquiries && e.preventDefault()}
                        className={`${styles.createPollButton} ${disabledUpdateInquiries ? styles.disabled : ''} anchorButton`}
                    >
                        {t('create_poll')} <CgPlayListAdd />
                    </Link>
                </div>
            </Toolbar>

            <ErrorSpinnerWrapper
                error={error}
                loading={isLoading}
            >
                <Table
                    columns={baseColumns}
                    rows={filteredPolls}
                    nohoverRow
                />
            </ErrorSpinnerWrapper>
        </div>
    );
};

export default PollsPage;